'use client';

import type { GfMaterialCountsResult } from '@/features/gfWorks/api/getMaterialCounts';
import type { MaterialCategoryNameForApi } from '@/features/gfWorks/api/getMaterialListByTypeInfinite';
import type { GfMaterialCategoryItem } from '@/features/gfWorks/types';
import { swal } from '@/lib/sweetalert';
import { pickErrorMessages } from '@/utils/functions';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMaterialsByType } from '../api/getMaterialListByTypeInfinite';

// コマ種別フィルター廃止（GF-2181）に伴い、取得カテゴリーは「すべて」固定
const MATERIAL_CATEGORY_ALL: MaterialCategoryNameForApi = 'all';

/**
 * 指定されたカテゴリーの素材データをAPIから取得する
 * @param params 取得条件（作品ID、カテゴリー名、取得件数、最後の素材ID等）
 * @returns 取得した素材カテゴリーデータ、取得できない場合はnull
 */
async function fetchCategory(params: {
  workId: number;
  categoryName: MaterialCategoryNameForApi;
  limit: number;
  lastMaterialId?: number;
  tagIds?: number[];
  gfProductId?: number;
}): Promise<GfMaterialCategoryItem | null> {
  const { result } = await getMaterialsByType({
    gfWorkId: params.workId,
    categoryName: params.categoryName,
    limit: params.limit,
    direction: 'next',
    lastMaterialId: params.lastMaterialId,
    tagIds: params.tagIds,
    gfProductId: params.gfProductId,
  });
  const category =
    result.categories.find((c) => c.categoryName === params.categoryName) ?? result.categories[0] ?? null;
  return category;
}

export type UseMaterialLoaderParams = {
  workId: number;
  initialAll: GfMaterialCategoryItem | null;
  pageLimit: number;
  selectedTagId: number | null;
  selectedProductId: number | null;
  materialCounts: GfMaterialCountsResult;
};

export function useMaterialLoader({
  workId,
  initialAll,
  pageLimit,
  selectedTagId,
  selectedProductId,
  materialCounts,
}: UseMaterialLoaderParams) {
  const [cache, setCache] = useState<Record<string, GfMaterialCategoryItem>>({});
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());
  const [loadingMoreKeys, setLoadingMoreKeys] = useState<Set<string>>(new Set());

  // cacheの最新値をuseRefで管理することで、startLoadingの依存配列からcacheを除外できる
  // これにより、cache更新時にstartLoadingが再生成されず、useEffectの不要な再実行を防ぐ
  // 例: お気に入り更新でcacheが変わっても、useEffectは再実行されない
  const cacheRef = useRef(cache);
  cacheRef.current = cache;

  // 無限スクロールの二重実行を防ぐためのフラグ
  // IntersectionObserverは短時間に複数回コールバックを呼ぶため、
  // Reactの状態更新（setLoadingMoreKeys）を待たずに同期的にチェックする必要がある
  const isLoadingMoreRef = useRef(false);

  // 初回ロードの二重実行防止: 「この cacheKey で fetch を開始したか」を同期的に保持する
  const inFlightFetchKeyRef = useRef<string | null>(null);

  const cacheKey = useMemo(
    () => `${selectedTagId ?? 'none'}-${selectedProductId ?? 'none'}`,
    [selectedTagId, selectedProductId],
  );

  const apiParams = useMemo(
    () => ({
      workId,
      limit: pageLimit,
      tagIds: selectedTagId !== null ? [selectedTagId] : undefined,
      gfProductId: selectedProductId ?? undefined,
    }),
    [workId, pageLimit, selectedTagId, selectedProductId],
  );

  const currentCategory = cache[cacheKey];
  const currentMaterials = currentCategory?.materials ?? [];
  const hasMore = currentCategory?.hasMore ?? false;
  const totalCount = currentCategory?.totalCount ?? 0;

  // 初期ロード用: キャッシュが存在しない状態からのデータ取得中かどうか
  // ページ全体のローディング表示に使用（フィルター切り替え、初回ページロード時など）
  const isLoading = loadingKeys.has(cacheKey);

  // 無限スクロール用: 追加データの取得中かどうか
  // InfiniteScrollコンポーネント内のローディング表示に使用（既存データは表示したまま）
  const isLoadingMore = loadingMoreKeys.has(cacheKey);

  /**
   * 指定されたキャッシュキーのローディング状態を開始する
   * キャッシュが既に存在する場合は何もしない（初期ロード用）
   *
   * @param key キャッシュキー
   *
   * 注意: cache[key]の代わりにcacheRef.current[key]を使用することで、
   * 依存配列を空にしている。これにより、cache更新時に本関数が再生成されず、
   * useEffectの不要な再実行を防いでいる。
   */
  const startLoading = useCallback((key: string) => {
    if (cacheRef.current[key]) return;

    setLoadingKeys((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  /**
   * 指定されたキャッシュキーのローディング状態を終了する
   * @param key キャッシュキー
   */
  const stopLoading = useCallback((key: string) => {
    setLoadingKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  /**
   * コマデータの取得
   * フィルター条件が変更された際に、キャッシュがなければAPIから素材データを取得する
   * - キャッシュが存在する場合は何もしない
   * - 初期データ（フィルターなし）の場合はpropsで渡された初期データを使用
   * - カウントが0の場合は空データをキャッシュ
   * - それ以外はAPIから取得
   */
  useEffect(() => {
    // キャッシュが存在する場合は何もしない
    if (cache[cacheKey]) {
      stopLoading(cacheKey);
      return;
    }

    // initialAll はサーバーが現在の gfProductId 付きで取得済みなので、
    // 商品フィルター時もそのまま現在のキャッシュキーに使える（クライアント再フェッチを避ける）。
    // タグ絞り込みはサーバー初期取得に含まれないため除外する。
    const isInitialTarget = selectedTagId === null && initialAll;
    if (isInitialTarget) {
      setCache((prev) => ({ ...prev, [cacheKey]: initialAll }));
      stopLoading(cacheKey);
      inFlightFetchKeyRef.current = null;
      return;
    }

    if (inFlightFetchKeyRef.current === cacheKey) {
      return;
    }

    startLoading(cacheKey);
    inFlightFetchKeyRef.current = cacheKey;

    if (materialCounts.all === 0) {
      setCache((prev) => ({
        ...prev,
        [cacheKey]: { materials: [], categoryName: MATERIAL_CATEGORY_ALL, totalCount: 0, hasMore: false },
      }));
      stopLoading(cacheKey);
      return;
    }

    const fetchData = async () => {
      try {
        const category = await fetchCategory({ ...apiParams, categoryName: MATERIAL_CATEGORY_ALL });
        if (category !== null) {
          setCache((prev) => ({ ...prev, [cacheKey]: category }));
        } else {
          setCache((prev) => ({
            ...prev,
            [cacheKey]: { materials: [], categoryName: MATERIAL_CATEGORY_ALL, totalCount: 0, hasMore: false },
          }));
        }
      } catch (error) {
        void swal.messages({ messages: pickErrorMessages(error) });
        setCache((prev) => ({
          ...prev,
          [cacheKey]: { materials: [], categoryName: MATERIAL_CATEGORY_ALL, totalCount: 0, hasMore: false },
        }));
      } finally {
        stopLoading(cacheKey);
      }
    };

    fetchData();

    return () => {
      stopLoading(cacheKey);
    };
  }, [cacheKey, apiParams, materialCounts, initialAll, selectedTagId, selectedProductId]);

  /**
   * 無限スクロールで追加の素材データを読み込む
   * - 既にローディング中、追加データがない、または最後の素材が存在しない場合は何もしない
   * - 最後の素材IDを指定してAPIから次のページを取得
   * - 取得したデータを既存のキャッシュに追加
   *
   * 注意: IntersectionObserverは短時間に複数回コールバックを呼ぶため、
   * isLoadingMoreRef で同期的にガードし、二重実行を防いでいる
   */
  const loadMore = useCallback(async () => {
    const lastMaterial = currentMaterials[currentMaterials.length - 1];

    // 同期的なチェック（useRef）+ 非同期的なチェック（state）の二重ガード
    if (isLoadingMoreRef.current || loadingMoreKeys.has(cacheKey) || !hasMore || !lastMaterial) {
      return;
    }

    // 即座にフラグを立てる（同期的、IntersectionObserverの連続呼び出しをブロック）
    isLoadingMoreRef.current = true;

    // 無限スクロール専用のローディング状態を設定（UI用、非同期）
    setLoadingMoreKeys((prev) => {
      if (prev.has(cacheKey)) return prev;
      const next = new Set(prev);
      next.add(cacheKey);
      return next;
    });

    try {
      const newCategory = await fetchCategory({
        ...apiParams,
        categoryName: MATERIAL_CATEGORY_ALL,
        lastMaterialId: lastMaterial.id,
      });

      if (newCategory) {
        setCache((prev) => {
          const base = prev[cacheKey];
          if (!base) return prev;
          return {
            ...prev,
            [cacheKey]: {
              ...base,
              materials: [...base.materials, ...newCategory.materials],
              hasMore: newCategory.hasMore,
              totalCount: newCategory.totalCount,
            },
          };
        });
      }
    } catch (error) {
      void swal.messages({ messages: pickErrorMessages(error) });
    } finally {
      // フラグを下げる（同期的）
      isLoadingMoreRef.current = false;

      // ローディング状態を解除（UI用、非同期）
      setLoadingMoreKeys((prev) => {
        if (!prev.has(cacheKey)) return prev;
        const next = new Set(prev);
        next.delete(cacheKey);
        return next;
      });
    }
  }, [apiParams, cacheKey, currentMaterials, hasMore, loadingMoreKeys]);

  /**
   * キャッシュ内の素材のお気に入り状態を更新する
   * - 全てのキャッシュキーを走査し、対象の素材が含まれていれば更新
   * - 複数のキャッシュ（異なるフィルター条件）に同じ素材が存在する可能性があるため全て更新
   * @param materialId 素材ID
   * @param isFavorite 新しいお気に入り状態
   */
  const updateFavoriteInCache = useCallback((materialId: number, isFavorite: boolean) => {
    setCache((prev) => {
      const next = { ...prev };
      let isModified = false;

      Object.keys(next).forEach((key) => {
        const category = next[key];
        if (!category?.materials.some((m) => m.id === materialId)) return;

        next[key] = {
          ...category,
          materials: category.materials.map((m) => (m.id === materialId ? { ...m, isFavorite } : m)),
        };
        isModified = true;
      });

      return isModified ? next : prev;
    });
  }, []);

  const allMaterialsFlat = useMemo(() => Object.values(cache).flatMap((c) => c.materials), [cache]);

  /**
   * つくりたいアイテムで絞り込むフィルターを切り替えた際に、即座にローディング状態を設定する
   * URLパラメータ更新（useTransition）とは別に、UI上で即座にローディング表示を開始するために使用
   * @param productId 商品ID（nullの場合はフィルター解除）
   */
  const setLoadingForProductId = useCallback(
    (productId: number | null) => {
      const targetCacheKey = `${selectedTagId ?? 'none'}-${productId ?? 'none'}`;

      startLoading(targetCacheKey);
    },
    [selectedTagId, startLoading],
  );

  return {
    currentMaterials,
    hasMore,
    totalCount,
    isLoading, // 初期ロード中: キャッシュがない状態からのデータ取得
    isLoadingMore, // 無限スクロール中: 追加データの取得
    loadMore,
    updateFavoriteInCache,
    allMaterialsFlat,
    setLoadingForProductId,
  };
}
